import { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";

const HISTORY_PAGE_SIZE = 30;
const TYPING_EXPIRY_MS = 4000;
const TYPING_SEND_THROTTLE_MS = 2000;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;
// R171: sem isso, nada no cliente mandava {type:"ping"} — o handler "pong" já
// existia no backend (chat_ws.py) mas nunca era exercitado. O nginx (R169) já
// cobre o timeout do proxy (proxy_read_timeout 3600s), mas qualquer outro
// intermediário (LB, CDN) com timeout de idle mais curto ainda podia derrubar
// a conexão em silêncio — a reconexão automática disfarçava o sintoma (só um
// "Reconectando..." rápido), sem resolver o desligamento por inatividade.
const PING_INTERVAL_MS = 30000;

/** Deriva a URL do WebSocket a partir da mesma REACT_APP_API_URL usada pelo
 * axios (`services/api.js`) — absoluta em dev (http://host:porta), relativa
 * em produção (`/api`, resolvida pelo Caddy contra a origem do próprio site). */
function buildWsUrl(token) {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  let base;
  if (/^https?:\/\//i.test(apiUrl)) {
    base = apiUrl.replace(/^http/i, "ws");
  } else {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const path = apiUrl.startsWith("/") ? apiUrl : `/${apiUrl}`;
    base = `${wsProtocol}//${window.location.host}${path}`;
  }
  base = base.replace(/\/$/, "");
  return `${base}/ws/chat?token=${encodeURIComponent(token)}`;
}

/**
 * useChat
 *
 * Hook de WebSocket do chat da comunidade: conexão com reconexão
 * automática (backoff exponencial), histórico paginado via REST e
 * indicador de "digitando".
 *
 * @param {object} opts
 * @param {number|null} opts.roomId
 * @param {function} opts.onIncomingMessage - (message) => void, chamado a cada
 *   mensagem recebida (própria ou de outros) — usado p/ badge de não-lido.
 */
export function useChat({ roomId, onIncomingMessage }) {
  const [status, setStatus] = useState("idle"); // idle | connecting | open | closed
  const [messages, setMessages] = useState([]);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]); // [{ userId, userName }]

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  // Conta quantas vezes consecutivas o handshake foi rejeitado (403 / token inválido).
  // Após MAX_HANDSHAKE_FAILS tentativas sem nenhum onopen, para de reconectar.
  const handshakeFailCountRef = useRef(0);
  const MAX_HANDSHAKE_FAILS = 3;
  const typingTimersRef = useRef(new Map()); // userId -> timeoutId
  const lastTypingSentAtRef = useRef(0);

  // Refs "sempre atuais" — evitam reabrir a conexão WS quando essas props
  // mudam, e evitam closures obsoletas dentro dos handlers do socket (mesmo
  // padrão usado para o loop infinito de UserAdminModal.onUpdated).
  const onIncomingMessageRef = useRef(onIncomingMessage);
  onIncomingMessageRef.current = onIncomingMessage;

  const clearTypingTimer = useCallback((userId) => {
    const timers = typingTimersRef.current;
    const existing = timers.get(userId);
    if (existing) {
      clearTimeout(existing);
      timers.delete(userId);
    }
  }, []);

  const handleServerEventRef = useRef(null);
  handleServerEventRef.current = (data) => {
    switch (data.type) {
      case "message": {
        const message = data;
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
        setTypingUsers((prev) => prev.filter((t) => t.userId !== message.user_id));
        clearTypingTimer(message.user_id);
        onIncomingMessageRef.current?.(message);
        break;
      }
      case "typing": {
        const { user_id: userId, user_name: userName } = data;
        setTypingUsers((prev) => {
          if (prev.some((t) => t.userId === userId)) return prev;
          return [...prev, { userId, userName }];
        });
        clearTypingTimer(userId);
        const timeoutId = setTimeout(() => {
          setTypingUsers((prev) => prev.filter((t) => t.userId !== userId));
          typingTimersRef.current.delete(userId);
        }, TYPING_EXPIRY_MS);
        typingTimersRef.current.set(userId, timeoutId);
        break;
      }
      case "joined":
      case "left":
      case "pong":
      case "error":
      default:
        break;
    }
  };

  // Conexão WS — ciclo de vida preso a um único efeito, reaberto apenas
  // quando a sala muda (não a cada render).
  useEffect(() => {
    const typingTimers = typingTimersRef.current;

    if (!roomId) {
      setStatus("idle");
      return undefined;
    }

    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("token");
      if (!token) {
        setStatus("closed");
        return;
      }
      setStatus("connecting");

      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      let opened = false;

      ws.onopen = () => {
        if (cancelled) return;
        opened = true;
        handshakeFailCountRef.current = 0;
        reconnectAttemptRef.current = 0;
        setStatus("open");
        ws.send(JSON.stringify({ type: "join", room_id: roomId }));

        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        handleServerEventRef.current?.(data);
      };

      ws.onclose = () => {
        clearInterval(pingIntervalRef.current);
        if (cancelled) return;
        setStatus("closed");
        setTypingUsers([]);

        if (!opened) {
          // Handshake rejeitado (ex: 403 token inválido) — não reconectar
          // indefinidamente com o mesmo token inútil.
          handshakeFailCountRef.current += 1;
          if (handshakeFailCountRef.current >= MAX_HANDSHAKE_FAILS) {
            return; // para o loop
          }
        }

        scheduleReconnect();
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(pingIntervalRef.current);
      typingTimers.forEach((timeoutId) => clearTimeout(timeoutId));
      typingTimers.clear();

      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
    };
  }, [roomId]);

  // Histórico inicial — recarrega do zero sempre que a sala muda.
  useEffect(() => {
    if (!roomId) {
      setMessages([]);
      setHasMoreHistory(true);
      return undefined;
    }

    let ignore = false;
    setMessages([]);
    setHasMoreHistory(true);
    setLoadingHistory(true);

    api
      .get(`/chat/rooms/${roomId}/messages`, { params: { limit: HISTORY_PAGE_SIZE } })
      .then((res) => {
        if (ignore) return;
        setMessages(res.data);
        setHasMoreHistory(res.data.length >= HISTORY_PAGE_SIZE);
      })
      .catch(() => {})
      .finally(() => {
        if (!ignore) setLoadingHistory(false);
      });

    return () => {
      ignore = true;
    };
  }, [roomId]);

  const loadOlderMessages = useCallback(() => {
    if (!roomId || loadingHistory || !hasMoreHistory) return;
    const beforeId = messages[0]?.id;
    if (!beforeId) return;

    setLoadingHistory(true);
    api
      .get(`/chat/rooms/${roomId}/messages`, {
        params: { limit: HISTORY_PAGE_SIZE, before_id: beforeId },
      })
      .then((res) => {
        setHasMoreHistory(res.data.length >= HISTORY_PAGE_SIZE);
        setMessages((prev) => [...res.data, ...prev]);
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [roomId, messages, loadingHistory, hasMoreHistory]);

  // mentionUserIds: ids escolhidos explicitamente no autocomplete do
  // composer (ver ChatModal.jsx) - nunca inferidos re-parseando o texto.
  const sendMessage = useCallback((content, mentionUserIds) => {
    const trimmed = (content || "").trim();
    if (!trimmed) return false;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({
      type: "message",
      content: trimmed,
      mention_user_ids: mentionUserIds && mentionUserIds.length ? mentionUserIds : undefined,
    }));
    return true;
  }, []);

  const sendTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_SEND_THROTTLE_MS) return;
    lastTypingSentAtRef.current = now;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "typing" }));
    }
  }, []);

  return {
    status,
    messages,
    hasMoreHistory,
    loadingHistory,
    typingUsers,
    loadOlderMessages,
    sendMessage,
    sendTyping,
  };
}
